// GENERAL
import React from "react";

// UTILS
import { getFontAwesomeIcon } from "../../../../../utils";

// COMPONENTS
import {
	BaseToolTip,
	BaseButton,
	BasePill,
	BaseText,
	StatusColorDot,
	SecondaryButton,
} from "../../../../../components";

// TYPES
import { OrderDetailsModalHeaderProps } from "../types";
const OrderDetailsModalHeader: React.FC<OrderDetailsModalHeaderProps> = ({
	dateOrder,
	orderNumber,
	orderStatus,
	handleClose,
	toggleMenu,
	fields,
}) => {
	// TEXT FROM FIELDS //
	const orderPillLabel = fields?.modalHeader?.orderBadge?.label;
	const orderPillPrefix = fields?.modalHeader?.orderBadge?.prefix;
	const viewLogButton = fields?.modalHeader?.viewLog?.label;
	const viewLogPrefix = fields?.modalHeader?.viewLog?.prefix;
	const openInNewTabButton = fields?.modalHeader?.openInNewTabButton?.label;
	const openInNewTabIcon = fields?.modalHeader?.openInNewTabButton?.prefix;
	const closeButton = fields?.modalHeader?.closeButton?.label;
	const closeIcon = fields?.modalHeader?.closeButton?.prefix;
	const orderDateLabel = fields?.modalHeader?.orderDate?.label;
	const orderNumberLabel = fields?.modalHeader?.orderNumber?.label;

	// SETTINGS FROM FIELDS //
	const isViewLogButtonVisible = fields?.modalHeader?.viewLog?.isVisible;

	// CLASS NAMES //

	const headerContainerClassNames =
		"flex flex-col items-between justify-between sticky top-[-5px] bg-white z-20 max-lg:flex-col min-h-[62px] pb-8";
	const headerTitleAndButtonsContainerClassNames =
		"flex items-start max-lg:flex-col justify-between w-full pb-2";
	const headerTitlesContainerClassNames =
		"flex w-2/3 max-lg:w-full max-lg:ml-1";
	const headerButtonContainerClassNames =
		"flex w-1/3 justify-end  max-lg:pt-3 max-lg:justify-start";
	const mobileExitButtonClassNames =
		"hidden max-lg:flex w-full justify-end py-2";

	return (
		<div className="">
			<header
				className={headerContainerClassNames}
				aria-label="Order Details modal header."
			>
				<div className={mobileExitButtonClassNames}>
					{
						<>
							{isViewLogButtonVisible && (
								<SecondaryButton
									text={viewLogButton}
									iconClasses="pr-2 max-sm:pr-0  text-primary"
									icon={getFontAwesomeIcon(
										viewLogPrefix,
										"regular",
									)}
									iconBreakpoint="max-lg:pr-0"
									textBreakpoint="max-lg:hidden"
									onClick={toggleMenu}
								/>
							)}
							<BaseToolTip
								message={openInNewTabButton}
								toolTipPlacement="top"
								children={
									<BaseButton
										text=""
										bgColor="bg-white"
										additionalClasses={`p-2 mx-4px`}
										shape="rounded-full"
										iconAriaLabel={openInNewTabButton}
										icon={getFontAwesomeIcon(
											openInNewTabIcon,
											"regular",
										)}
										as="button"
										onClick={() =>
											window.open(
												window.location.href,
												"_blank",
											)
										}
										iconClasses="flex items-center text-lg pr-0 size-3 text-navy-300 hover:text-navy-500"
									/>
								}
							/>
							<BaseToolTip
								message={closeButton}
								toolTipPlacement="top"
								children={
									<BaseButton
										text=""
										bgColor="bg-white"
										additionalClasses={`p-2 mx-4px`}
										shape="rounded-full"
										iconAriaLabel={closeButton}
										icon={getFontAwesomeIcon(
											closeIcon,
											"regular",
										)}
										as="button"
										onClick={handleClose}
										iconClasses="flex items-center text-lg pr-0 size-3 text-navy-300 hover:text-navy-500"
									/>
								}
							/>
						</>
					}
				</div>
				<div className="flex flex-col">
					<div className={headerTitleAndButtonsContainerClassNames}>
						<div className={headerTitlesContainerClassNames}>
							{
								<BasePill
									text={
										<BaseText
											size={"text-sm"}
											color={"text-navy-500"}
											text={orderPillLabel}
											fontFamily={"font-omnes-medium"}
											tag={"h1"}
										/>
									}
									firstIcon={getFontAwesomeIcon(
										orderPillPrefix,
										"solid",
									)}
									hoverBackgroundColor="none"
									additionalClasses="px-2.5 py-1 h-26px mr-1.5"
									shape="rounded-full"
									color="bg-supply-blue-50"
									borderColor="transparent"
									type={"span"}
									hasMobileStyle={false}
								/>
							}
							<div className="flex items-center">
								<div className="flex items-center font-omnes-regular">
									<h2 className="flex items-center text-sm mt-0">
										<span className="text-navy-350 pr-.5">
											{orderDateLabel}
										</span>
										<span className="text-navy-500 pl-1 mr-3">
											{dateOrder}
										</span>
									</h2>
									<StatusColorDot
										status={orderStatus}
										additionalClasses="flex items-center font-omnes-regular"
										useConvertedStatus={true}
										fontFamily="font-omnes-regular text-sm"
									/>
								</div>
							</div>
						</div>
						<div className={headerButtonContainerClassNames}>
							<div className="flex max-lg:hidden items-center">
								{isViewLogButtonVisible ? (
									<SecondaryButton
										text={viewLogButton}
										iconClasses="pr-2 max-sm:pr-0 text-primary"
										icon={getFontAwesomeIcon(
											viewLogPrefix,
											"regular",
										)}
										onClick={toggleMenu}
									/>
								) : null}
								<BaseToolTip
									message={openInNewTabButton}
									toolTipPlacement="top"
									children={
										<BaseButton
											bgColor="bg-white"
											additionalClasses={`p-2 mx-4px`}
											iconAriaLabel={openInNewTabButton}
											icon={getFontAwesomeIcon(
												openInNewTabIcon,
												"regular",
											)}
											as="button"
											onClick={() =>
												window.open(
													window.location.href,
													"_blank",
												)
											}
											iconClasses="flex items-center text-lg pr-0 size-3 hover:text-navy-500 text-navy-300"
										/>
									}
								/>

								<BaseToolTip
									message={closeButton}
									toolTipPlacement="top"
									children={
										<BaseButton
											text=""
											bgColor="bg-white"
											additionalClasses={`p-2 mx-4px`}
											shape="rounded-full"
											iconAriaLabel={closeButton}
											icon={getFontAwesomeIcon(
												closeIcon,
												"regular",
											)}
											as="button"
											onClick={handleClose}
											iconClasses="flex items-center text-lg pr-0 size-3 hover:text-navy-500 text-navy-300"
										/>
									}
								/>
							</div>
						</div>
					</div>
					<div className="flex items-start">
						<BaseText
							size={"text-22px"}
							color={"text-navy-500"}
							text={`${orderNumberLabel} ${orderNumber}`}
							fontFamily={"font-omnes-medium"}
							tag={"h1"}
						/>
					</div>
				</div>
			</header>
		</div>
	);
};

export default OrderDetailsModalHeader;
